<template>
  <header
    class="bg-white h-[94px] fixed top-0 w-screen z-[10000] px-s max-mobile:px-4 max-mobile:h-[64px]"
    ref="headerRef"
  >
    <Menubar
      :model="header?.items"
      :unstyled="true"
      v-bind="menuPassThrough"
      :class="{
        'menu-mobile-active': isMobileMenuActive,
        'is-mobile-view group': isMobileView,
      }"
      :data-mobile-view="isMobileView ? true : null"
      v-on-click-outside="
        () => {
          isMobileMenuActive = false
        }
      "
    >
      <template #start>
        <NuxtLink
          :to="getPagePath['home'][locale]"
          @click="handleLogoClick"
          class="shrink-0"
          :aria-label="$t('dbc-button-return-to-home')"
        >
          <UtilityImage
            :src="logo?.url"
            :alt="`${clientMap[client]} Logo`"
            :width="logo?.width"
            :height="logo?.height"
            :logo="true"
          />
        </NuxtLink>
      </template>
      <template #item="{ item, props, hasSubmenu, root }">
        <NuxtLink
          @mouseover="
            () => {
              if (root) {
                header?.items.forEach((i) => {
                  if (item !== i) {
                    i.isOpen = false
                  }
                })
              }
            }
          "
          :class="{
            'active-main-menu-item after:bg-primary after:h-[4px] after:w-full after:absolute after:left-0 after:bottom-0':
              route.fullPath === item.url && root && !isMobileView,
            'font-bold': route.fullPath === item.url,
            'bg-gray': item.isOpen,
          }"
          v-bind="props.action"
          :to="
            hasSubmenu && Object.keys(hasSubmenu).length > 0 ? null : item.url
          "
          :target="resolveLinkTarget(item.url)"
          @click="
            () => {
              if (
                (hasSubmenu && hasSubmenu.length === 0) ||
                hasSubmenu === undefined
              ) {
                toggleMainMenu()
              }
            }
          "
          v-on-click-outside="
            () => {
              item.isOpen = false
            }
          "
        >
          <i v-if="item.icon" class="dbc-gc-btn__icon">
            <component
              :is="isPreview() ? 'img' : NuxtImg"
              width="24"
              height="24"
              :src="`${useRuntimeConfig().public.cms}${item.icon.media.url}`"
            />
          </i>
          <span>{{ item.label }}</span>
          <DbcSvg
            v-if="hasSubmenu && Object.keys(hasSubmenu).length > 0"
            class="duration-200 group-aria-expanded/item:rotate-180"
            name="dbc-icon-chevron-down"
          />
        </NuxtLink>
      </template>
      <template #end>
        <div class="flex h-full">
          <div class="flex h-full">
            <HeaderLanguageSelect
              v-if="locales && locales.length > 0"
              :routes="translatedRoutes"
            />
          </div>
          <NuxtLink
            :to="selfServiceUrl"
            :target="resolveLinkTarget(selfServiceUrl)"
            :aria-label="$t('dbc-text-self-service')"
            class="flex items-center justify-center hover:bg-gray w-[48px] cursor-pointer text-dark"
            :class="{
              'border-solid border-0 border-b-4 border-primary':
                route.fullPath.includes(getPagePath['self-service']![locale]!) ||
                route.fullPath.includes(getPagePath['login']![locale]!),
            }"
          >
            <DbcSvg name="dbc-icon-account" class="block" />
          </NuxtLink>
          <div
            v-if="header && header.ctaButton && !route.fullPath.includes(getPagePath['self-service']![locale]!)"
            class="flex items-center ml-2xs max-mobile:hidden"
          >
            <NuxtLink
              class="dbc-gc-btn dbc-gc-btn--primary whitespace-nowrap"
              :to="header.ctaButton.link"
              :target="resolveLinkTarget(header.ctaButton.link)"
            >
              {{ header.ctaButton.text }}
            </NuxtLink>
          </div>
          <div
            :class="{
              'max-mobile:flex': isMobileMenuActive,
            }"
            class="hidden fixed bottom-10 left-5 flex-col gap-2"
          >
            <UtilityAppsBadge
              v-for="app in computeAppsByOs"
              :fixedHeight="true"
              :key="app.id"
              :app="app"
            />
          </div>
        </div>
      </template>
      <template #button>
        <button
          :class="{
            'bg-gray': isMobileMenuActive,
            'bg-white': !isMobileMenuActive,
            hidden: !isMobileView,
          }"
          class="border-none h-full order-last right-xs max-mobile:h-[64px] max-mobile:items-center max-mobile:flex"
          @click="toggleMainMenu"
          tabindex="0"
          aria-label="Navigation"
          aria-haspopup="true"
          :aria-expanded="isMobileMenuActive"
        >
          <DbcSvg
            :name="isMobileMenuActive ? 'dbc-close' : 'dbc-burger-menu'"
          />
        </button>
      </template>
    </Menubar>
  </header>
</template>

<script setup lang="ts">
import { vOnClickOutside } from '@vueuse/components'
import Menubar from 'primevue/menubar'
import { NuxtImg } from '#components'

const props = defineProps<{
  page: Page
  logo?: Image
}>()

const { locale, locales } = useI18n()
const route = useRoute()

const client = useRuntimeConfig().public.client
const clientMap = useRuntimeConfig().public.clientMapping as ClientMapping

const header = (await useFetchTheHeader()).value?.[locale.value]

const isMobileMenuActive = ref(false)

const headerRef = ref<HTMLInputElement | null>(null)
const addHeaderShadowOnScroll = () => {
  const scroll = window.scrollY
  scroll > 0
    ? headerRef.value?.classList.add('shadow-lg')
    : headerRef.value?.classList.remove('shadow-lg')
}

function closeSubMenu() {
  header?.items.forEach((i) => {
    i.isOpen = false
  })
  const submenus = document.querySelectorAll(
    '[data-pc-section="submenu"]'
  ) as NodeListOf<HTMLElement>
  submenus.forEach((submenu: HTMLElement) => {
    submenu.style.display = 'none'
  })
}

const handleScroll = () => {
  addHeaderShadowOnScroll()
  closeSubMenu()
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  let timeout: ReturnType<typeof setTimeout>
  window.addEventListener('resize', () => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(handleResize, 100)
  })
  calculateIsBurgerMenu()
})

function handleLogoClick() {
  window.scrollTo(0, 0)
}

const mobileOs = useMobileOs()
const selfServiceUrl = header?.selfServiceLink
  ? header?.selfServiceLink
  : mobileOs.value === ''
  ? getPagePath['data']![locale.value]
  : getPagePath['self-service']![locale.value]

const translatedRoutes: TranslatedRoute[] = []
locales.value?.forEach(({ code }) => {
  const translatedPage = props.page?.localizations?.find(
    (localization) => localization.locale === code
  )
  translatedRoutes.push({
    locale: code,
    path: translatedPage
      ? translatedPage.path
      : getPagePath['home']![code]!.replace(/\/de\/|\/en\//g, ''),
  })
})

function toggleMainMenu() {
  isMobileMenuActive.value = !isMobileMenuActive.value
}

const apps = inject('apps') as Ref<App[] | null>
const computeAppsByOs = computed(() => {
  if (apps.value && mobileOs.value !== '') {
    return apps.value.filter((app) => app.os === mobileOs.value)
  }

  return apps.value
})

const isMobileView = ref(false)
async function calculateIsBurgerMenu() {
  isMobileView.value = false
  const logo = document.querySelector('[data-pc-section="start"]')
  const logoPosition = logo?.getBoundingClientRect()
  const nav = document.querySelector('[data-pc-section="rootlist"]')
  const navPosition = nav?.getBoundingClientRect()

  if (navPosition && logoPosition) {
    const distance = navPosition.left - logoPosition.right
    isMobileView.value = distance <= 24
  }
}

const handleResize = () => {
  calculateIsBurgerMenu()
}

const menuPassThrough = {
  pt: {
    root: {
      class:
        'flex flex-row justify-between data-[mobile-view="true"]:justify-normal w-full h-full items-center group-[.is-mobile-view]:relative',
    },
    rootList: {
      class:
        'flex flex-row gap-4 m-0 h-full group-[.is-mobile-view]:h-auto group-[.menu-mobile-active.is-mobile-view]:flex group-[.is-mobile-view]:hidden items-center group-[.is-mobile-view]:absolute group-[.menu-mobile-active.is-mobile-view]:top-[94px] group-[.is-mobile-view]:top-[-100vh] group-[.is-mobile-view]:right-0 group-[.is-mobile-view]:max-mobile:left-0 group-[.is-mobile-view]:flex-col group-[.is-mobile-view]:gap-0 group-[.is-mobile-view]:w-[430px] group-[.is-mobile-view]:pb-xs group-[.is-mobile-view]:bg-gray group-[.is-mobile-view]:pl-0 group-[.is-mobile-view]:shadow-md group-[.menu-mobile-active.is-mobile-view]:max-mobile:top-[64px] group-[.is-mobile-view]:max-mobile:w-screen group-[.is-mobile-view]:max-mobile:h-[calc(100vh-64px)]',
    },
    item: {
      class:
        'group/item relative list-none no-underline h-full flex items-center data-[p-focused="true"]:bg-gray hover:bg-gray cursor-pointer group-[.is-mobile-view]:bg-gray group-[.is-mobile-view]:w-full group-[.is-mobile-view]:px-xs group-[.is-mobile-view]:flex group-[.is-mobile-view]:flex-col group-[.is-mobile-view]:max-mobile:h-auto',
    },
    itemContent: {
      class:
        'h-full whitespace-nowrap cursor-pointer group-[.is-mobile-view]:border-solid group-[.is-mobile-view]:border-0 group-[.is-mobile-view]:border-b group-[.is-mobile-view]:border-light group-[.is-mobile-view]:w-full group-[.is-mobile-view]:block group-[.is-mobile-view]:hover:bg-medium',
    },
    itemLink: {
      class:
        'no-underline h-full text-dark p-xs cursor-pointer relative flex items-center gap-2',
    },
    start: {
      class: 'flex group-[.is-mobile-view]:mr-auto',
    },
    end: {
      class: 'h-full',
    },
    subMenu: {
      class:
        'flex min-w-full flex-col absolute top-[94px] pl-0 bg-white shadow-md group-[.is-mobile-view]:static group-[.is-mobile-view]:w-full group-[.is-mobile-view]:shadow-none group-[.is-mobile-view]:max-mobile:top-[64px]',
    },
  },
}
</script>
